/**
 * Verify Email Pending Page
 * Shown after signup to prompt user to verify their email
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { auth } from '../config/firebaseInit';
import { sendCustomEmailVerification } from '../services/WebCloudFunctionsService';

const VerifyEmailPendingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  useEffect(() => {
    // Get email and display name from location state or Firebase Auth
    const stateEmail = (location.state as any)?.email;
    const stateDisplayName = (location.state as any)?.displayName;
    const firebaseUser = auth.currentUser;

    if (stateEmail) {
      setEmail(stateEmail);
    } else if (firebaseUser?.email) {
      setEmail(firebaseUser.email);
    }

    if (stateDisplayName) {
      setDisplayName(stateDisplayName);
    } else if (firebaseUser?.displayName) {
      setDisplayName(firebaseUser.displayName);
    }

    // Check if email is already verified (user might have verified while on this page)
    const checkVerification = () => {
      if (firebaseUser?.emailVerified) {
        navigate('/home', { replace: true });
      }
    };

    // Check immediately
    checkVerification();

    // Check periodically (every 5 seconds)
    const interval = setInterval(checkVerification, 5000);

    return () => clearInterval(interval);
  }, [location.state, navigate]);

  const handleResendVerification = async () => {
    setIsResending(true);
    setResendSuccess(false);
    setResendError(null);

    try {
      const firebaseUser = auth.currentUser;
      if (!firebaseUser || !firebaseUser.email) {
        throw new Error('No user found. Please sign up again.');
      }

      // Use custom email verification (Brevo)
      await sendCustomEmailVerification(firebaseUser.email, firebaseUser.uid, firebaseUser.displayName || undefined);

      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error: any) {
      console.error('Failed to resend verification email:', error);
      setResendError(error.message || 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await auth.signOut();
      navigate('/admin/login', { replace: true });
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Check Your Email
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            We've sent a verification link to
          </p>
          <p className="text-gray-900 dark:text-white font-semibold mt-1">
            {email || 'your email'}
          </p>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/50 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-blue-900 dark:text-blue-200">
                Please click the verification link in the email to activate your account. 
                You won't be able to access all features until your email is verified.
              </p>
            </div>
          </div>
        </div>

        {resendSuccess && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 rounded-xl p-4 mb-6">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
              <p className="text-sm text-emerald-900 dark:text-emerald-200">
                Verification email resent successfully!
              </p>
            </div>
          </div>
        )}

        {resendError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-xl p-4 mb-6">
            <div className="flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-900 dark:text-red-200">
                {resendError}
              </p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleResendVerification}
            disabled={isResending}
            className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isResending ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Mail className="w-5 h-5" />
                <span>Resend Verification Email</span>
              </>
            )}
          </button>

          <button
            onClick={handleSignOut}
            className="w-full px-4 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-xl font-semibold transition-colors duration-200"
          >
            Sign Out
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Didn't receive the email? Check your spam folder or try resending.
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPendingPage;


